import Item from '~/shared/src/ci/Item';
import {dataExtraction} from '~/shared/src/utilities/dataExtraction';
import metaModel from '~/shared/src/utilities/metaModel';
import dataUtils from '~/shared/src/utilities/dataUtils';
import {
    PropertyKeyType,
    PropertyValueType
} from '~/shared/src/mm/PropertySet';
import uuidGenerator from '~/shared/src/utilities/uuidGenerator';

type LabelXmlJson = { '@_language' : string, '@_for'? : string, '#cdata' : string };
type IconXmlJson = { '@_type' : 'imageData' | 'cssClass', '@_size'? : '1x' | '2x', '#cdata'? : string, '#text'? : string };

export type ValueSetXmlJson = {
    origin : string;
    module : string;
    description? : string | { '#cdata' : string };
    value? : ValueXmlJson[];
    '@_name' : string;
    '@_status' : string;
    '@_uuid' : string;
};

export type ValueXmlJson = {
    origin : string;
    module : string;
    label? : LabelXmlJson[];
    '@_name' : string;
    '@_order' : string;
    '@_status' : string;
    '@_uuid' : string;
};

export type ItemTypeXmlJson = {
    '@_name' : string;
    '@_status' : string;
    '@_uuid' : string;
    origin : string;
    module : string;
    oldName? : string;
    description? : string | { '#cdata' : string };
    keyDescription? : string | { '#cdata' : string };
    keyDefinition? : string | { '#cdata' : string };
    nameDescription? : string | { '#cdata' : string };
    nameDefinition? : string | { '#cdata' : string };
    expiryDays? : number;
    displayField : 'key' | 'name';
    shape? : string;
    color? : string;
    style? : string;
    isKeyAutogenerated : boolean;
    isImported : boolean;
    icon? : IconXmlJson[];
    propertySet? : PropertySetXmlJson[];
    id? : number;
    label? : LabelXmlJson[];
};

export type PropertyTypeXmlJson = {
    origin : string;
    module : string;
    '@_name' : string;
    '@_status' : string;
    '@_uuid' : string;
};

export type PropertySetXmlJson = {
    '@_status' : string;
    '@_uuid' : string;
    origin : string;
    module : string;
    propertyType : string;
    oldPropertyType? : string;
    valueSet? : string;
    description? : string | { '#cdata' : string };
    keyType? : PropertyKeyType;
    keyDefinition? : string | { '#cdata' : string };
    keyExample? : string | { '#cdata' : string };
    valueType? : PropertyValueType;
    valueDefinition? : string | { '#cdata' : string };
    valueDefault? : string;
    valueExample? : string | { '#cdata' : string };
    valueMin? : string;
    valueMax? : string;
    valueUnit? : string;
    isEnumerated : boolean;
    isImported : boolean;
    label? : LabelXmlJson[];
};

export type ReferenceTypeXmlJson = {
    origin : string;
    module : string;
    '@_name' : string;
    '@_status' : string;
    '@_uuid' : string;
};

export type ReferenceSetXmlJson = {
    origin : string;
    module : string;
    referenceType : string;
    '@_status' : string;
    '@_uuid' : string;
    sourceItemType : string;
    destinationItemType : string;
    cardinality : string;
    description? : string | { '#cdata' : string };
    propertySet? : PropertySetXmlJson[];
    isImported : boolean;
}

const createCdata = ( data : string | undefined ) : string | { '#cdata' : string } | undefined => {
    return data !== undefined ? { '#cdata': data } : undefined;
}

/**
 * Get XML JSON notation for ItemType of YnItemType item
 *
 * @param {Item} ynItemTypeItem
 * @param {Item} [typeLabelItem]
 * @param {Item} [keyLabelItem]
 * @param {Item} [nameLabelItem]
 * @param {Item} [iconItem]
 *
 * @return {ItemTypeXmlJson}
 */
export const getItemTypeXmlJson = ( ynItemTypeItem : Item, typeLabelItem? : Item, keyLabelItem? : Item, nameLabelItem? : Item, iconItem? : Item, forExport : boolean = false ) : ItemTypeXmlJson => {
    const extractedItemTypeItem = dataExtraction.extractPropertiesOfItem( ynItemTypeItem ),
          typeLabel             = typeLabelItem ? getLabel( typeLabelItem, 'type' ) : undefined,
          keyLabel              = keyLabelItem ? getLabel( keyLabelItem, 'key' ) : undefined,
          nameLabel             = nameLabelItem ? getLabel( nameLabelItem, 'name' ) : undefined;

    let label : LabelXmlJson[] | undefined,
        oldName : string | undefined;

    if ( !!typeLabel || !!keyLabel || !!nameLabel ) {
        label = ( keyLabel ?? [] ).concat( nameLabel ?? [] )
                                  .concat( typeLabel ?? [] );
    }

    if ( ynItemTypeItem.attributesModified.has( 'name' ) ) {
        oldName = ynItemTypeItem.attributesModified.get( 'name' ) as string ?? undefined;
    }

    return {
        origin            : extractedItemTypeItem.origin,
        module            : extractedItemTypeItem.module,
        oldName,
        description       : forExport ? createCdata( extractedItemTypeItem.description ) : extractedItemTypeItem.description,
        keyDescription    : forExport ? createCdata( extractedItemTypeItem.keyDescription ) : extractedItemTypeItem.keyDescription,
        keyDefinition     : forExport ? createCdata( extractedItemTypeItem.keyDefinition ) : extractedItemTypeItem.keyDefinition,
        nameDescription   : forExport ? createCdata( extractedItemTypeItem.nameDescription ) : extractedItemTypeItem.nameDescription,
        nameDefinition    : forExport ? createCdata( extractedItemTypeItem.nameDefinition ) : extractedItemTypeItem.nameDefinition,
        expiryDays        : extractedItemTypeItem.expiryDays,
        displayField      : extractedItemTypeItem.displayField === 'CI_NAME' ? 'name' : 'key',
        isKeyAutogenerated: extractedItemTypeItem.isKeyAutogenerated,
        isImported        : extractedItemTypeItem.isImported,
        shape             : extractedItemTypeItem.shape,
        color             : extractedItemTypeItem.color,
        style             : extractedItemTypeItem.style,
        label,
        icon              : iconItem ? getIcon( iconItem ) : undefined,
        '@_name'          : extractedItemTypeItem.name,
        '@_status'        : getItemTypeXmlJsonStatus( ynItemTypeItem.statusId ),
        '@_uuid'          : uuidGenerator.getUuidByTypeAndPlaceholders(
            'ItemType',
            {
                Origin      : extractedItemTypeItem.origin,
                Module      : extractedItemTypeItem.module,
                Schema      : extractedItemTypeItem.schema,
                ItemTypeName: extractedItemTypeItem.name
            }
        )
    }
};

/**
 * Get XML JSON notation for PropertyType of YnPropertyType item
 *
 * @param {Item} ynPropertySetItem
 *
 * @return {PropertyTypeXmlJson}
 */
export const getPropertyTypeXmlJson = ( ynPropertySetItem : Item ) : PropertyTypeXmlJson => {
    const extractedPropertySetItem = dataExtraction.extractPropertiesOfItem( ynPropertySetItem ),
          schema                   = ( extractedPropertySetItem.schema as string ).toUpperCase() as Schema;

    return {
        origin    : extractedPropertySetItem.origin,
        module    : extractedPropertySetItem.module,
        '@_name'  : extractedPropertySetItem.propertyTypeName,
        '@_status': metaModel[ schema ].getStatus( extractedPropertySetItem.status )?.name ?? 'active',
        '@_uuid'  : uuidGenerator.getUuidByTypeAndPlaceholders(
            'PropertyType',
            {
                Origin          : extractedPropertySetItem.origin,
                Module          : extractedPropertySetItem.module,
                Schema          : extractedPropertySetItem.schema,
                PropertyTypeName: extractedPropertySetItem.propertyTypeName
            }
        )
    }
};

/**
 * Get XML JSON notation for ValueSet of YnValueSet item
 *
 * @param {Item} ynValueSetItem
 *
 * @return {ValueSetXmlJson}
 */
export const getValueSetXmlJson = ( ynValueSetItem : Item, forExport : boolean = false ) : ValueSetXmlJson => {
    const extractedValueSetItem = dataExtraction.extractPropertiesOfItem( ynValueSetItem ),
          schema                = ( extractedValueSetItem.schema as string ).toUpperCase() as Schema;

    return {
        origin     : extractedValueSetItem.origin,
        module     : extractedValueSetItem.module,
        description: forExport ? createCdata( extractedValueSetItem.description ) : extractedValueSetItem.description,
        '@_name'   : extractedValueSetItem.name,
        '@_status' : metaModel[ schema ].getStatus( extractedValueSetItem.status )?.name ?? 'active',
        '@_uuid'   : uuidGenerator.getUuidByTypeAndPlaceholders(
            'ValueSet',
            {
                Origin      : extractedValueSetItem.origin,
                Module      : extractedValueSetItem.module,
                Schema      : extractedValueSetItem.schema,
                ValueSetName: extractedValueSetItem.name
            }
        )
    }
};

/**
 * Get XML JSON notation for ValueSet of YnValueSet item
 *
 * @param {Item} ynValueItem
 * @param {Item} [valueLabelItem]
 *
 * @return {ValueXmlJson}
 */
export const getValueXmlJson = ( ynValueSetItem : Item, ynValueItem : Item, valueLabelItem? : Item, forExport : boolean = false ) : ValueXmlJson => {
    const extractedValueSetItem = dataExtraction.extractPropertiesOfItem( ynValueSetItem ),
          extractedValueItem    = dataExtraction.extractPropertiesOfItem( ynValueItem ),
          schema                = ( extractedValueItem.schema as string ).toUpperCase() as Schema,
          label                 = valueLabelItem ? getLabel( valueLabelItem ) : undefined;

    return {
        origin    : extractedValueItem.origin,
        module    : extractedValueItem.module,
        label,
        '@_name'  : extractedValueItem.name,
        '@_order' : extractedValueItem.sortorder,
        '@_status': metaModel[ schema ].getStatus( extractedValueItem.status )?.name ?? 'active',
        '@_uuid'  : uuidGenerator.getUuidByTypeAndPlaceholders(
            'Value',
            {
                Origin      : extractedValueItem.origin,
                Module      : extractedValueItem.module,
                Schema      : extractedValueItem.schema,
                ValueSetName: extractedValueSetItem.name,
                Value       : extractedValueItem.name
            }
        )
    }
};

/**
 * Get XML JSON notation for PropertySet of YnPropertySet item
 *
 * @param {Item} ynPropertySetItem
 * @param {Item} [typeLabelItem]
 * @param {Item} [valueLabelItem]
 * @param {Item} [keyLabelItem]
 * @param {string} [valueSetName]
 * @param {Item} [ynItemTypeItem]
 *
 * @return {PropertySetXmlJson}
 */
export const getPropertySetXmlJson = ( ynPropertySetItem : Item, typeLabelItem? : Item, valueLabelItem? : Item, keyLabelItem? : Item, valueSetName? : string, ynItemTypeItem? : Item, forExport : boolean = false ) : PropertySetXmlJson => {
    const extractedPropertySetItem = dataExtraction.extractPropertiesOfItem( ynPropertySetItem ),
          propertyTypeId           = metaModel.CFG.getPropertyType( 'PropertyTypeName' )?.id,
          typeLabel                = typeLabelItem ? getLabel( typeLabelItem, 'type' ) : undefined,
          keyLabel                 = keyLabelItem ? getLabel( keyLabelItem, 'key' ) : undefined,
          valueLabel               = valueLabelItem ? getLabel( valueLabelItem, 'value' ) : undefined;

    let label : LabelXmlJson[] | undefined,
        oldPropertyType : string | undefined;

    if ( !!typeLabel || !!keyLabel || !!valueLabel ) {
        label = ( keyLabel ?? [] ).concat( valueLabel ?? [] ).concat( typeLabel ?? [] );
    }

    if ( propertyTypeId && ynPropertySetItem.properties.get( propertyTypeId )?.isModified() ) {
        oldPropertyType = ynPropertySetItem.properties.get( propertyTypeId )?.attributesModified.get( 'value' ) as string ?? undefined;
    }

    return {
        origin         : extractedPropertySetItem.origin,
        module         : extractedPropertySetItem.module,
        propertyType   : extractedPropertySetItem.propertyTypeName,
        oldPropertyType,
        valueSet       : valueSetName,
        description    : forExport ? createCdata( extractedPropertySetItem.description ) : extractedPropertySetItem.description,
        valueType      : extractedPropertySetItem.valueType,
        valueDefinition: forExport ? createCdata( extractedPropertySetItem.valueDefinition ) : extractedPropertySetItem.valueDefinition,
        valueDefault   : extractedPropertySetItem.valueDefault,
        valueExample   : forExport ? createCdata( extractedPropertySetItem.valueExample ) : extractedPropertySetItem.valueExample,
        valueMin       : extractedPropertySetItem.valueMin,
        valueMax       : extractedPropertySetItem.valueMax,
        valueUnit      : extractedPropertySetItem.valueUnit,
        isEnumerated   : extractedPropertySetItem.isEnumerated,
        keyType        : extractedPropertySetItem.keyType,
        keyDefinition  : forExport ? createCdata( extractedPropertySetItem.keyDefinition ) : extractedPropertySetItem.keyDefinition,
        keyExample     : forExport ? createCdata( extractedPropertySetItem.keyExample ) : extractedPropertySetItem.keyExample,
        isImported     : extractedPropertySetItem.isImported,
        label,
        '@_status'     : getItemTypeXmlJsonStatus( ynPropertySetItem.statusId ),
        '@_uuid'       : uuidGenerator.getUuidByTypeAndPlaceholders(
            'PropertySet',
            {
                Origin          : extractedPropertySetItem.origin,
                Module          : extractedPropertySetItem.module,
                Schema          : extractedPropertySetItem.schema,
                ItemTypeName    : ynItemTypeItem?.name,
                PropertyTypeName: extractedPropertySetItem.propertyTypeName
            }
        )
    }
};

/**
 * Get XML JSON notation for ReferenceType of YnReferenceSet item
 *
 * @param {Item} ynReferenceSetItem
 *
 * @return {ReferenceTypeXmlJson}
 */
export const getReferenceTypeXmlJson = ( ynReferenceSetItem : Item ) : ReferenceTypeXmlJson => {
    const extractedReferenceSetItem = dataExtraction.extractPropertiesOfItem( ynReferenceSetItem ),
          schema                    = ( extractedReferenceSetItem.schema as string ).toUpperCase() as Schema;

    return {
        origin    : extractedReferenceSetItem.origin,
        module    : extractedReferenceSetItem.module,
        '@_name'  : extractedReferenceSetItem.referenceTypeName,
        '@_status': metaModel[ schema ].getStatus( extractedReferenceSetItem.status )?.name ?? 'active',
        '@_uuid'  : uuidGenerator.getUuidByTypeAndPlaceholders(
            'ReferenceType',
            {
                Origin           : extractedReferenceSetItem.origin,
                Module           : extractedReferenceSetItem.module,
                Schema           : extractedReferenceSetItem.schema,
                ReferenceTypeName: extractedReferenceSetItem.referenceTypeName
            }
        )
    }
};

/**
 * Get XML JSON notation for ReferenceSet of YnReferenceSet item
 *
 * @param {Item} ynReferenceSetItem
 * @param {string} dataContext
 *
 * @return {ReferenceSetXmlJson}
 */
export const getReferenceSetXmlJson = ( ynReferenceSetItem : Item, dataContext : string, forExport : boolean = false ) : ReferenceSetXmlJson => {
    const extractedReferenceSetItem                                             = dataExtraction.extractPropertiesOfItem( ynReferenceSetItem ),
          [ defaultSrcObjectType, defaultReferenceType, defaultDestObjectType ] = extractedReferenceSetItem.name.split( ':' ),
          isSourceObjectTypeRsetId                                              = metaModel.CFG.getReferenceSetsForItemTypesAndReferenceType( 'YnItemType', 'YnReferenceSet', 'isSourceObjectType', 'src' )[ 0 ].id,
          isSourceObjectTypeId                                                  = dataUtils.findReferences( dataContext, isSourceObjectTypeRsetId, undefined, ynReferenceSetItem.id, true, false )?.[ 0 ]?.srcItemId ?? 0,
          srcObjectType                                                         = global.data[ dataContext ].items[ isSourceObjectTypeId ]?.name ?? defaultSrcObjectType,
          isDestObjectTypeRsetId                                                = metaModel.CFG.getReferenceSetsForItemTypesAndReferenceType( 'YnItemType', 'YnReferenceSet', 'isDestObjectType', 'src' )[ 0 ].id,
          isDestObjectTypeId                                                    = dataUtils.findReferences( dataContext, isDestObjectTypeRsetId, undefined, ynReferenceSetItem.id, true, false )?.[ 0 ]?.srcItemId ?? 0,
          destObjectType                                                        = global.data[ dataContext ].items[ isDestObjectTypeId ]?.name ?? defaultDestObjectType;

    return {
        origin             : extractedReferenceSetItem.origin,
        module             : extractedReferenceSetItem.module,
        referenceType      : extractedReferenceSetItem.referenceTypeName ?? defaultReferenceType,
        sourceItemType     : srcObjectType,
        destinationItemType: destObjectType,
        cardinality        : extractedReferenceSetItem.cardinality,
        description        : forExport ? createCdata( extractedReferenceSetItem.description ) : extractedReferenceSetItem.description,
        isImported         : extractedReferenceSetItem.isImported,
        '@_status'         : getItemTypeXmlJsonStatus( ynReferenceSetItem.statusId ),
        '@_uuid'           : uuidGenerator.getUuidByTypeAndPlaceholders(
            'ReferenceSet',
            {
                Origin                 : extractedReferenceSetItem.origin,
                Module                 : extractedReferenceSetItem.module,
                Schema                 : extractedReferenceSetItem.schema,
                ItemTypeNameSource     : srcObjectType,
                ReferenceTypeName      : extractedReferenceSetItem.referenceTypeName ?? defaultReferenceType,
                ItemTypeNameDestination: destObjectType
            }
        )
    }
};

/**
 * Get status name for id. Obey delete notation as used in xmlImport
 *
 * @param {number} statusId
 *
 * @return {string}
 */
const getItemTypeXmlJsonStatus = ( statusId : number ) : string => {
    let status = metaModel.DATA.getStatus( statusId )?.name ?? 'active';

    if ( status === 'deleted' ) {
        status = 'delete';
    }

    return status;
};

/**
 * Get status name for id. Obey delete notation as used in xmlImport
 *
 * @param {Item} messageItem
 * @param {string} [labelFor]
 *
 * @return {LabelXmlJson[] | undefined}
 */
const getLabel = ( messageItem : Item, labelFor? : string ) : LabelXmlJson[] | undefined => {
    const extractedMessageItem = dataExtraction.extractPropertiesOfItem( messageItem );

    let retVal : LabelXmlJson[] | undefined;

    if ( extractedMessageItem.content ) {
        retVal = Object.keys( extractedMessageItem.content ).map( language => ( {
            '@_language': language as string,
            '@_for'     : labelFor,
            '#cdata'    : extractedMessageItem.content[ language ] as string
        } ) );
    }

    return retVal;
};

/**
 * Get status name for id. Obey delete notation as used in xmlImport
 *
 * @param {Item} imageItem
 *
 * @return {IconXmlJson[] | undefined}
 */
const getIcon = ( imageItem : Item ) : IconXmlJson[] | undefined => {
    const extractedMessageItem = dataExtraction.extractPropertiesOfItem( imageItem );

    let retVal : IconXmlJson[] | undefined;

    if ( extractedMessageItem.content ) {
        retVal = Object.keys( extractedMessageItem.content ).map( size => ( {
            '@_type': 'imageData',
            '#cdata': extractedMessageItem.content[ size ] as string
        } ) );
    }

    if ( extractedMessageItem.cssClass ) {
        retVal = ( retVal ?? [] ).concat( {
            '@_type': 'cssClass',
            '#cdata': extractedMessageItem.cssClass as string
        } );
    }

    return retVal;
};
